import { el } from "./dom";

export interface CopyButtonOptions {
  /** Resolves the text to copy at click time, so callers can pass dynamic values. */
  readonly getText: () => string;
  readonly label: string;
  /** Optional label flashed in place of `label` after a successful copy. */
  readonly copiedLabel?: string;
  /** Optional extra class names to apply to the rendered button. */
  readonly className?: string;
}

const COPIED_FLASH_MS = 1400;

async function writeClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy execCommand path
    }
  }
  // Legacy fallback — synthesize a hidden <textarea>, select, exec copy.
  // Works on older WebKit + non-secure contexts where the Clipboard API is gated.
  if (typeof document === "undefined") return false;
  const node = document.createElement("textarea");
  node.value = text;
  node.setAttribute("readonly", "");
  node.style.position = "fixed";
  node.style.top = "-9999px";
  node.style.opacity = "0";
  document.body.appendChild(node);
  node.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  document.body.removeChild(node);
  return ok;
}

/**
 * Copy-to-clipboard button with a transient "copied" state. The button label
 * swaps to `copiedLabel` for ~1.4s after a successful write, then reverts.
 */
export function createCopyButton(opts: CopyButtonOptions): HTMLButtonElement {
  const classes = ["crest-btn", "crest-btn--small"];
  if (opts.className) classes.push(opts.className);

  let timer: number | null = null;

  const btn = el("button", {
    class: classes.join(" "),
    attrs: { type: "button" },
  }, [opts.label]) as HTMLButtonElement;

  btn.addEventListener("click", () => {
    void writeClipboard(opts.getText()).then((ok) => {
      if (!ok) return;
      btn.textContent = opts.copiedLabel ?? opts.label;
      btn.classList.add("crest-btn--copied");
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        btn.textContent = opts.label;
        btn.classList.remove("crest-btn--copied");
        timer = null;
      }, COPIED_FLASH_MS);
    });
  });

  return btn;
}
